import { get } from 'lodash';
import React from 'react';
import PropTypes from 'prop-types';
import { RootConsumer } from '../../RootComponent';
import documentStyles from '../../Document/Document.scss';
import { Button, Modal, Progress } from 'semantic-ui-react';
import { MobileModal } from '../../Modules';
import Formsy from 'formsy-react';

export default class Wizard extends React.Component {
  static propTypes = {
    closeIcon: PropTypes.bool,
    closeOnDimmerClick: PropTypes.bool,
    modalSize: PropTypes.string,
    onOpen: PropTypes.func,
    onClose: PropTypes.func,
    open: PropTypes.bool,
    showProgress: PropTypes.bool,
    steps: PropTypes.arrayOf(
      PropTypes.shape({
        component: PropTypes.oneOfType([PropTypes.func, PropTypes.object])
          .isRequired,
        componentProps: PropTypes.object,
        hideActions: PropTypes.bool,
        title: PropTypes.string,
        actions: PropTypes.shape({
          primary: PropTypes.shape({
            content: PropTypes.string,
            type: PropTypes.string,
            disabled: PropTypes.bool
          }),
          secondary: PropTypes.shape({
            content: PropTypes.string,
            hide: PropTypes.bool
          })
        }),
        formProps: PropTypes.shape({
          onSubmit: PropTypes.func,
          onValidSubmit: PropTypes.func,
          onInvalidSubmit: PropTypes.func
        })
      })
    ).isRequired,
    trigger: PropTypes.node
  };

  constructor(props) {
    super(props);
    this.state = {
      activeStep: 0,
      open: false,
      submitted: false
    };
    this.form = React.createRef();
    this.onSubmit = this.onSubmit.bind(this);
    this.onValidSubmit = this.onValidSubmit.bind(this);
    this.onInvalidSubmit = this.onInvalidSubmit.bind(this);
    this.onPrimaryButtonClick = this.onPrimaryButtonClick.bind(this);
    this.onSecondaryButtonClick = this.onSecondaryButtonClick.bind(this);
    this.hasPreviousStep = this.hasPreviousStep.bind(this);
    this.hasNextStep = this.hasNextStep.bind(this);
    this.primaryButtonText = this.primaryButtonText.bind(this);
    this.secondaryButtonText = this.secondaryButtonText.bind(this);
    this.progress = this.progress.bind(this);
    this.next = this.next.bind(this);
    this.previous = this.previous.bind(this);
    this.openModal = this.openModal.bind(this);
    this.closeModal = this.closeModal.bind(this);
    this.title = this.title.bind(this);
  }

  onSubmit(model, resetForm, invalidateForm) {
    this.setState({ submitted: true });
    const onSubmit = get(
      this.props.steps[this.state.activeStep],
      'formProps.onSubmit'
    );
    const callback = this.hasNextStep() ? this.next : this.closeModal;
    if (onSubmit) {
      onSubmit(model, resetForm, invalidateForm, callback);
    }
  }

  onValidSubmit(model, resetForm, invalidateForm) {
    this.setState({ submitted: true });
    const onValidSubmit = get(
      this.props.steps[this.state.activeStep],
      'formProps.onValidSubmit'
    );
    const callback = this.hasNextStep() ? this.next : this.closeModal;
    if (onValidSubmit) {
      onValidSubmit(model, resetForm, invalidateForm, callback);
    }
  }

  onInvalidSubmit(...props) {
    this.setState({ submitted: true });
    const onInvalidSubmit = get(
      this.props.steps[this.state.activeStep],
      'formProps.onInvalidSubmit'
    );
    if (onInvalidSubmit) onInvalidSubmit(...props);
  }

  onSecondaryButtonClick() {
    return this.hasPreviousStep() ? this.previous() : this.closeModal();
  }

  onPrimaryButtonClick() {
    const type = get(this.currentStep().actions, 'primary.type');
    if (type === 'submit') {
      this.form.current.submit();
    } else if (this.hasNextStep()) {
      this.next();
    } else {
      this.closeModal();
    }
  }

  secondaryButtonText() {
    const content = get(this.currentStep(), 'actions.secondary.content');
    if (content) {
      return content;
    } else if (this.hasPreviousStep()) {
      return 'Back';
    }
    return 'Cancel';
  }

  primaryButtonText() {
    const content = get(this.currentStep(), 'actions.primary.content');
    if (content) {
      return content;
    } else if (this.hasNextStep()) {
      return 'Next';
    }
    return 'Finish';
  }

  progress() {
    return (this.state.activeStep + 1) / this.props.steps.length * 100;
  }

  currentStep() {
    return this.props.steps[this.state.activeStep];
  }

  hasNextStep() {
    return this.state.activeStep < this.props.steps.length - 1;
  }

  hasPreviousStep() {
    return this.state.activeStep > 0;
  }

  next() {
    if (this.hasNextStep()) {
      this.setState(prevState => ({
        activeStep: prevState.activeStep + 1,
        submitted: false
      }));
    }
  }

  previous() {
    if (this.hasPreviousStep()) {
      this.setState(prevState => ({
        activeStep: prevState.activeStep - 1,
        submitted: false
      }));
    }
  }

  title() {
    return this.props.steps[this.state.activeStep].title;
  }

  openModal() {
    this.setState({ open: true, activeStep: 0, submitted: false });
    if (this.props.onOpen) this.props.onOpen();
  }

  closeModal() {
    this.setState({ open: false, activeStep: 0 });
    if (this.props.onClose) this.props.onClose();
  }

  render() {
    const {
      closeIcon,
      closeOnDimmerClick,
      modalSize,
      showProgress,
      trigger
    } = this.props;
    const { submitted } = this.state;
    const open = this.props.open || this.state.open;
    const progressBar = <Progress percent={this.progress()} indicating />;
    const {
      actions,
      componentProps,
      formProps,
      hideActions
    } = this.currentStep();
    const Component = this.currentStep().component;
    const modalContent = (
      <Formsy.Form
        {...formProps}
        ref={this.form}
        onSubmit={this.onSubmit}
        onValidSubmit={this.onValidSubmit}
        onInvalidSubmit={this.onInvalidSubmit}
      >
        {showProgress && progressBar}
        <Component
          {...componentProps}
          goBack={this.previous}
          goForward={this.next}
          onClose={this.closeModal}
          submitted={submitted}
        />
      </Formsy.Form>
    );
    const hideSecondaryButton = get(actions, 'secondary.hide') && true;
    return (
      <RootConsumer>
        {({ isMobile }) => [
          trigger ? React.cloneElement(trigger, {
            onClick: this.openModal,
            key: 'trigger'
          }) : null,
          !isMobile &&
            <Modal
              onClose={this.closeModal}
              onOpen={this.openModal}
              open={open}
              size={modalSize || 'small'}
              key='desktop'
              closeIcon={closeIcon}
              closeOnDimmerClick={closeOnDimmerClick}
            >
              <Modal.Header content={this.title()} />
              <Modal.Content>
                {modalContent}
              </Modal.Content>
              {!hideActions &&
                <Modal.Actions>
                  {!hideSecondaryButton &&
                    <a
                      onClick={this.onSecondaryButtonClick}
                      className={documentStyles.modalSecondaryAction}
                    >
                      {this.secondaryButtonText()}
                    </a>}
                  <Button
                    primary
                    onClick={this.onPrimaryButtonClick}
                    disabled={get(actions, 'primary.disabled')}
                  >
                    {this.primaryButtonText()}
                  </Button>
                </Modal.Actions>}
            </Modal>,
          isMobile &&
            <MobileModal
              onClose={this.closeModal}
              onOpen={this.openModal}
              open={open}
              key='mobile'
            >
              {!hideActions &&
                <MobileModal.Header>
                  <div className='menu'>
                    {!hideSecondaryButton &&
                      <a className='item' onClick={this.onSecondaryButtonClick}>
                        {this.secondaryButtonText()}
                      </a>}
                  </div>
                </MobileModal.Header>}
              <MobileModal.Content key='content'>
                {modalContent}
                {!hideActions &&
                  <Button
                    primary
                    disabled={get(actions, 'primary.disabled')}
                    style={{ marginTop: '2em' }}
                    onClick={this.onPrimaryButtonClick}
                  >
                    {this.primaryButtonText()}
                  </Button>}
              </MobileModal.Content>
            </MobileModal>
        ]}
      </RootConsumer>
    );
  }
}



// WEBPACK FOOTER //
// ./webpack/components/Modules/Wizard/Wizard.js